import { formatMoney } from '../shared/format/money.ts';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Loader2, AlertCircle, FileText, Eye, Sparkles, Minus, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import axiosInstance from '../api/axios';

const fadeInStyle = `
@keyframes evidenceFadeIn {
  from { opacity: 0; transform: scale(0.97); }
  to { opacity: 1; transform: scale(1); }
}
.evidence-fade-in {
  animation: evidenceFadeIn 0.35s ease-out forwards;
}
`;

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 8;

function buildFindingsText(stepFindings) {
  if (!stepFindings) return '';
  const parts = [];
  if (stepFindings.findings_summary) parts.push(stepFindings.findings_summary);
  if (stepFindings.detailed_findings) parts.push(stepFindings.detailed_findings);
  if (stepFindings.red_flags?.length > 0) {
    parts.push('Red flags: ' + stepFindings.red_flags.join('; '));
  }
  if (stepFindings.exceptions?.length > 0) {
    const excSummary = stepFindings.exceptions
      .slice(0, 3)
      .map(e => `${e.record_ref}: ${e.issue}${e.amount ? ` (${formatMoney(e.amount)})` : ''}`)
      .join('; ');
    parts.push('Exceptions: ' + excSummary);
  }
  return parts.join('\n\n');
}

/** The message for a citation to a page the PDF does not have (null when the page is in the file or unknown). */
export function missingPageMessage(pageNumber, totalPages) {
  if (!pageNumber || !totalPages || pageNumber <= totalPages) return null;
  return `Page ${pageNumber} is not in this file (it has ${totalPages} page${totalPages === 1 ? '' : 's'}).`;
}

/** Body for /api/evidence/screenshot and /annotate, for a given page of the cited file. */
export function buildScreenshotRequest(citation, executionId, pageNumber) {
  return {
    file_id: citation.source_file_id,
    file_ref: citation.file_ref || null,
    filename: citation.source_file || '',
    citation: {
      source_file: citation.source_file || '',
      source_file_id: citation.source_file_id,
      sheet_name: citation.sheet_name || null,
      page_number: pageNumber ?? (citation.page_number || null),
      row_numbers: citation.row_numbers || null,
      cell_ref: citation.cell_ref || null,
      excerpt: citation.excerpt || '',
    },
    execution_id: executionId ? String(executionId) : null,
  };
}

/** The annotation saved at run time for this page (blob URL), or null when none exists yet. */
export async function fetchSavedAnnotation(citation, executionId, pageNumber) {
  if (!executionId) return null;
  try {
    const resp = await axiosInstance.post('/api/evidence/saved-annotation',
      buildScreenshotRequest(citation, executionId, pageNumber),
      { responseType: 'arraybuffer', timeout: 30000 });
    if (resp.status === 200 && resp.data?.byteLength > 0) {
      return URL.createObjectURL(new Blob([resp.data], { type: 'image/png' }));
    }
  } catch (err) {
    console.warn('Saved annotation lookup failed (falling back to on-demand):', err.message);
  }
  return null;
}

/** A citation that is probably into a PDF (by name, or because the AI gave a page). */
function looksLikePdf(citation) {
  return /\.pdf$/i.test(citation?.source_file || '') || !!citation?.page_number;
}

function EvidenceViewer({ citation, isOpen, onClose, stepFindings, executionId }) {
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [screenshotLoading, setScreenshotLoading] = useState(false);
  const [screenshotError, setScreenshotError] = useState(null);

  const [annotatedUrl, setAnnotatedUrl] = useState(null);
  const [annotationLoading, setAnnotationLoading] = useState(false);

  const [viewMode, setViewMode] = useState('annotated');

  // PDF paging: the citation names one page (the one the AI used); the reader may want the
  // rest. `page` is the page on screen; other pages are plain screenshots, cached so that
  // flipping back and forth does not refetch.
  const citedPage = citation?.page_number || 1;
  const [page, setPage] = useState(citedPage);
  const [pageCount, setPageCount] = useState(null);
  // Pages in the cited PDF (also when there is only one): tells a page the file does not have from a failed load.
  const [pdfPages, setPdfPages] = useState(null);
  const [pageUrl, setPageUrl] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState(null);
  const pageCache = useRef(new Map());
  // Annotated versions of the non-cited pages (only pages that have findings of their own).
  const [pageAnnotUrl, setPageAnnotUrl] = useState(null);
  const [pageAnnotLoading, setPageAnnotLoading] = useState(false);
  const pageAnnotCache = useRef(new Map());

  // Zoom/pan state — single object so wheel updates are atomic
  const [view, _setView] = useState({ zoom: 1, panX: 0, panY: 0 });
  const viewRef = useRef(view);
  const setView = useCallback((updater) => {
    _setView(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      viewRef.current = next;
      return next;
    });
  }, []);

  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ active: false, startX: 0, startY: 0, initPanX: 0, initPanY: 0 });

  const containerRef = useRef(null);
  const lastFetchedKey = useRef(null);

  // Key that identifies a unique evidence location for caching / dedup.
  // Prefer source_file_id (integer DB key); fall back to file_ref (GCS path)
  // so citations resolved only by file_ref (e.g. AI citations without a DB id)
  // still trigger the fetch.
  const citationKey = citation?.source_file_id
    ? `${citation.source_file_id}-${citation.page_number || ''}-${citation.sheet_name || ''}-${(citation.row_numbers || []).join(',')}`
    : citation?.file_ref
      ? `ref:${citation.file_ref}-${citation.page_number || ''}-${citation.sheet_name || ''}`
      : null;

  const resetView = useCallback(() => {
    setView({ zoom: 1, panX: 0, panY: 0 });
  }, [setView]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
      if (annotatedUrl) URL.revokeObjectURL(annotatedUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close on escape
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Wheel: scrolls a zoomed page (moves it up/down, or sideways with shift); Ctrl/⌘ + wheel or a trackpad pinch
  // zooms. Non-passive so we can preventDefault.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !isOpen) return;
    const onWheel = (e) => {
      // A zoomed page: the wheel moves it (within the page's edges). Not zoomed: the wheel zooms, as before, so a
      // mouse without Ctrl still zooms in. Ctrl/⌘ + wheel (and a trackpad pinch) always zooms.
      const zoomedNow = viewRef.current.zoom > 1;
      if (!(e.ctrlKey || e.metaKey) && zoomedNow) {
        e.preventDefault();
        const dx = e.shiftKey ? e.deltaY : e.deltaX;
        const dy = e.shiftKey ? 0 : e.deltaY;
        const r = el.getBoundingClientRect();
        setView(v => {
          const maxX = (r.width * (v.zoom - 1)) / 2;
          const maxY = (r.height * (v.zoom - 1)) / 2;
          const clamp = (x, m) => Math.max(-m, Math.min(m, x));
          return { ...v, panX: clamp(v.panX - dx, maxX), panY: clamp(v.panY - dy, maxY) };
        });
        return;
      }
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.12 : 0.88;
      const rect = el.getBoundingClientRect();
      // Cursor position relative to container center
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;
      setView(v => {
        const nextZoom = Math.min(Math.max(v.zoom * factor, ZOOM_MIN), ZOOM_MAX);
        // Zoom toward cursor: keep the point under the cursor fixed
        return {
          zoom: nextZoom,
          panX: cx - ((cx - v.panX) * nextZoom / v.zoom),
          panY: cy - ((cy - v.panY) * nextZoom / v.zoom),
        };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [isOpen, setView]);

  // Window-level drag listeners
  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragRef.current.active) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setView(v => ({
        ...v,
        panX: dragRef.current.initPanX + dx,
        panY: dragRef.current.initPanY + dy,
      }));
    };
    const onMouseUp = () => {
      if (!dragRef.current.active) return;
      dragRef.current.active = false;
      setIsDragging(false);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [setView]);

  // Fetch evidence when modal opens with a NEW citation
  useEffect(() => {
    if (!isOpen || !citationKey) return;

    if (lastFetchedKey.current === citationKey && (screenshotUrl || screenshotError)) return;
    lastFetchedKey.current = citationKey;

    if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    if (annotatedUrl) URL.revokeObjectURL(annotatedUrl);

    setScreenshotUrl(null);
    setAnnotatedUrl(null);
    setScreenshotError(null);
    setViewMode('annotated');
    resetView();

    const requestBody = buildScreenshotRequest(citation, executionId);

    setScreenshotLoading(true);
    axiosInstance.post('/api/evidence/screenshot', requestBody, {
      responseType: 'arraybuffer',
      timeout: 60000,
    }).then(resp => {
      if (resp.status === 200 && resp.data?.byteLength > 0) {
        const blob = new Blob([resp.data], { type: 'image/png' });
        setScreenshotUrl(URL.createObjectURL(blob));
      } else {
        setScreenshotError('Could not generate screenshot');
      }
    }).catch(err => {
      console.error('Screenshot request failed:', err.message);
      setScreenshotError('Failed to load evidence screenshot');
    }).finally(() => setScreenshotLoading(false));

    const citedPageFindings = stepFindings?.page_findings?.[citedPage] || '';
    const baseFindings = buildFindingsText(stepFindings);
    const findingsText = citedPageFindings && !baseFindings.includes(citedPageFindings)
      ? [baseFindings, citedPageFindings].filter(Boolean).join('\n\n')
      : baseFindings;
    let cancelledAnnot = false;
    setAnnotationLoading(true);
    // Saved at run time -> instant. Otherwise generate on demand from the findings (and save that).
    fetchSavedAnnotation(citation, executionId, citedPage).then((savedUrl) => {
      if (cancelledAnnot) { if (savedUrl) URL.revokeObjectURL(savedUrl); return null; }
      if (savedUrl) { setAnnotatedUrl(savedUrl); return null; }
      if (!findingsText) return null;
      return axiosInstance.post('/api/evidence/annotate', {
        ...requestBody,
        findings_text: findingsText,
      }, {
        responseType: 'arraybuffer',
        timeout: 90000,
      }).then(resp => {
        if (!cancelledAnnot && resp.status === 200 && resp.data?.byteLength > 0) {
          const blob = new Blob([resp.data], { type: 'image/png' });
          setAnnotatedUrl(URL.createObjectURL(blob));
        }
      }).catch(err => {
        console.warn('Annotation request failed (non-blocking):', err.message);
      });
    }).finally(() => { if (!cancelledAnnot) setAnnotationLoading(false); });
    return () => { cancelledAnnot = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, citationKey]);

  const clearPageCache = useCallback(() => {
    pageCache.current.forEach((url) => URL.revokeObjectURL(url));
    pageCache.current = new Map();
    pageAnnotCache.current.forEach((url) => URL.revokeObjectURL(url));
    pageAnnotCache.current = new Map();
    setPageUrl(null);
    setPageAnnotUrl(null);
  }, []);

  // New citation: back to its cited page, and find out how many pages the file has.
  useEffect(() => {
    if (!isOpen || !citationKey) return undefined;
    let cancelled = false;
    setPage(citedPage);
    setPageCount(null);
    setPdfPages(null);
    setPageError(null);
    clearPageCache();

    if (looksLikePdf(citation) && (citation.source_file_id || citation.file_ref)) {
      axiosInstance.post('/api/evidence/page-count', {
        file_id: citation.source_file_id ?? null,
        file_ref: citation.file_ref || null,
        filename: citation.source_file || '',
      }).then((resp) => {
        const n = resp.data?.is_pdf ? Number(resp.data.page_count) : 0;
        if (!cancelled && n > 0) setPdfPages(n);
        if (!cancelled && n > 1) setPageCount(n);
      }).catch(() => { /* paging is an extra; the cited page still shows */ });
    }
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, citationKey]);

  // Another page of the PDF was chosen: fetch it (plain screenshot; annotations belong to the cited page).
  useEffect(() => {
    if (!isOpen || !citationKey || page === citedPage) return undefined;
    const cached = pageCache.current.get(page);
    const pageFindingsText = stepFindings?.page_findings?.[page] || '';
    setPageAnnotUrl(pageAnnotCache.current.get(page) || null);
    // Annotate this page too when it has findings of its own (e.g. line items matched on it).
    const annotatePage = (cancelledRef) => {
      if (pageAnnotCache.current.has(page)) return;
      setPageAnnotLoading(true);
      const keep = (url) => {
        pageAnnotCache.current.set(page, url);
        if (!cancelledRef.current) setPageAnnotUrl(url);
      };
      // Saved at run time -> instant; otherwise generate from this page's own findings (if any).
      fetchSavedAnnotation(citation, executionId, page).then((savedUrl) => {
        if (savedUrl) { keep(savedUrl); return null; }
        if (!pageFindingsText) return null;
        return axiosInstance.post('/api/evidence/annotate', {
          ...buildScreenshotRequest(citation, executionId, page),
          findings_text: pageFindingsText,
        }, { responseType: 'arraybuffer', timeout: 90000 }).then((resp) => {
          if (resp.status === 200 && resp.data?.byteLength > 0) {
            keep(URL.createObjectURL(new Blob([resp.data], { type: 'image/png' })));
          }
        });
      }).catch((err) => {
        console.warn('Page annotation request failed (non-blocking):', err.message);
      }).finally(() => { if (!cancelledRef.current) setPageAnnotLoading(false); });
    };
    if (cached) {
      setPageUrl(cached);
      setPageError(null);
      const ref = { current: false };
      annotatePage(ref);
      return () => { ref.current = true; };
    }
    let cancelled = false;
    const cancelledRef = { get current() { return cancelled; } };
    annotatePage(cancelledRef);
    setPageUrl(null);
    setPageError(null);
    setPageLoading(true);
    axiosInstance.post('/api/evidence/screenshot', buildScreenshotRequest(citation, executionId, page), {
      responseType: 'arraybuffer',
      timeout: 60000,
    }).then((resp) => {
      if (cancelled) return;
      if (resp.status === 200 && resp.data?.byteLength > 0) {
        const url = URL.createObjectURL(new Blob([resp.data], { type: 'image/png' }));
        pageCache.current.set(page, url);
        setPageUrl(url);
      } else {
        setPageError(`Could not load page ${page}`);
      }
    }).catch(() => {
      if (!cancelled) setPageError(`Failed to load page ${page}`);
    }).finally(() => { if (!cancelled) setPageLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, citationKey, page]);

  const goToPage = useCallback((n) => {
    if (!pageCount) return;
    const next = Math.min(Math.max(1, n), pageCount);
    setPage((cur) => {
      if (cur !== next) resetView();
      return next;
    });
  }, [pageCount, resetView]);

  // Left / right arrow keys turn pages.
  useEffect(() => {
    if (!isOpen || !pageCount) return undefined;
    const onKey = (e) => {
      if (e.target?.tagName === 'INPUT') return;
      if (e.key === 'ArrowLeft') goToPage(page - 1);
      if (e.key === 'ArrowRight') goToPage(page + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, pageCount, page, goToPage]);

  // Reset zoom + fetched key when modal closes
  useEffect(() => {
    if (!isOpen) {
      lastFetchedKey.current = null;
      resetView();
      clearPageCache();
    }
  }, [isOpen, resetView, clearPageCache]);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      initPanX: viewRef.current.panX,
      initPanY: viewRef.current.panY,
    };
    setIsDragging(true);
  }, []);

  if (!isOpen) return null;

  const onCitedPage = page === citedPage;
  const hasAnnotation = onCitedPage ? !!annotatedUrl : !!pageAnnotUrl;
  const annotationPending = onCitedPage ? annotationLoading : pageAnnotLoading;
  const zoomPct = Math.round(view.zoom * 100);
  const loadingNow = onCitedPage ? screenshotLoading : pageLoading;
  // A citation to a page past the end of the file gets no image (not another page in its place) and says so.
  const errorNow = onCitedPage
    ? (screenshotError && (missingPageMessage(citedPage, pdfPages) || screenshotError))
    : pageError;

  const zoomIn  = () => setView(v => ({ ...v, zoom: Math.min(v.zoom * 1.25, ZOOM_MAX) }));
  const zoomOut = () => setView(v => ({ ...v, zoom: Math.max(v.zoom / 1.25, ZOOM_MIN) }));

  // Determine which image src to show in the zoomable area
  let imgSrc = null;
  let imgAlt = '';
  let imgClass = 'max-w-full max-h-full object-contain rounded-lg shadow evidence-fade-in';

  if (!onCitedPage) {
    if (pageUrl && !pageLoading && !pageError) {
      if (viewMode === 'annotated' && pageAnnotUrl) {
        imgSrc = pageAnnotUrl;
        imgAlt = `AI-annotated page ${page}`;
        imgClass = 'max-w-full max-h-full object-contain rounded-lg shadow-lg border border-purple-200 evidence-fade-in';
      } else {
        imgSrc = pageUrl;
        imgAlt = `Page ${page}`;
      }
    }
  } else if (!screenshotLoading && !screenshotError && (citation?.source_file_id || citation?.file_ref)) {
    if (viewMode === 'annotated') {
      if (annotatedUrl) {
        imgSrc = annotatedUrl;
        imgAlt = 'AI-annotated evidence';
        imgClass = 'max-w-full max-h-full object-contain rounded-lg shadow-lg border border-purple-200 evidence-fade-in';
      } else if (screenshotUrl) {
        imgSrc = screenshotUrl;
        imgAlt = annotationLoading ? 'Evidence (annotation generating...)' : 'Evidence';
        imgClass = `max-w-full max-h-full object-contain rounded-lg shadow evidence-fade-in${annotationLoading ? ' opacity-60' : ''}`;
      }
    } else if (viewMode === 'original' && screenshotUrl) {
      imgSrc = screenshotUrl;
      imgAlt = 'Original evidence';
    }
  }

  return (
    <div data-evidence-viewer-open className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <style>{fadeInStyle}</style>
      <div className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-6xl h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="h-5 w-5 text-blue-500 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 truncate">{citation?.source_file || 'Evidence'}</h3>
              <p className="text-xs text-gray-500">
                {citation?.sheet_name && `Sheet: ${citation.sheet_name}`}
                {citation?.page_number && `Page ${citation.page_number}`}
                {citation?.row_numbers?.length > 0 && ` | Row${citation.row_numbers.length > 1 ? 's' : ''} ${citation.row_numbers.join(', ')}`}
                {citation?.excerpt && ` | "${citation.excerpt}"`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Page navigation — PDFs with more than one page */}
            {pageCount > 1 && (
              <div className="flex items-center gap-1 bg-gray-100 border border-gray-200 rounded-lg px-1 py-0.5" data-testid="page-nav">
                <button
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1}
                  className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30 rounded transition-colors"
                  title="Previous page (←)"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <span className="px-1 text-xs text-gray-700 whitespace-nowrap" aria-live="polite">
                  Page {page} of {pageCount}
                </span>
                <button
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= pageCount}
                  className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30 rounded transition-colors"
                  title="Next page (→)"
                  aria-label="Next page"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
                {citation?.page_number && !onCitedPage && (
                  <button
                    onClick={() => goToPage(citedPage)}
                    className="ml-1 px-2 py-0.5 text-[11px] font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100"
                    title="Back to the page the AI cited"
                  >
                    Cited: page {citedPage}
                  </button>
                )}
                {citation?.page_number && onCitedPage && (
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded">cited</span>
                )}
              </div>
            )}

            {/* Zoom controls */}
            <div className="flex items-center gap-0.5 bg-gray-100 border border-gray-200 rounded-lg px-1 py-0.5">
              <button
                onClick={zoomOut}
                disabled={view.zoom <= ZOOM_MIN}
                className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30 rounded transition-colors"
                title="Zoom out (scroll down)"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={resetView}
                className="px-2 py-0.5 text-xs font-mono text-gray-600 hover:text-gray-900 min-w-[3.5rem] text-center rounded hover:bg-gray-200 transition-colors"
                title="Reset zoom"
              >
                {zoomPct}%
              </button>
              <button
                onClick={zoomIn}
                disabled={view.zoom >= ZOOM_MAX}
                className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30 rounded transition-colors"
                title="Zoom in (scroll up)"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* View mode toggle */}
            {(hasAnnotation || annotationPending) && (
              <div className="flex bg-gray-200 rounded-lg p-0.5">
                <button
                  onClick={() => setViewMode('annotated')}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    viewMode === 'annotated' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title="AI-annotated view"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  AI Annotated
                </button>
                <button
                  onClick={() => setViewMode('original')}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    viewMode === 'original' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title="Original evidence screenshot"
                >
                  <Eye className="h-3.5 w-3.5" />
                  Original
                </button>
              </div>
            )}

            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content — overflow:hidden so zoomed image clips cleanly */}
        <div
          ref={containerRef}
          className="flex-1 overflow-hidden bg-gray-50 relative select-none"
          onMouseDown={handleMouseDown}
          style={{ cursor: isDragging ? 'grabbing' : view.zoom > 1 ? 'grab' : 'default' }}
        >
          {/* Loading state */}
          {loadingNow && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
              <p className="text-sm text-gray-500">{onCitedPage ? 'Loading evidence...' : `Loading page ${page}...`}</p>
            </div>
          )}

          {/* Error state */}
          {errorNow && !loadingNow && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <AlertCircle className="h-8 w-8 text-red-400" />
              <p className="text-sm text-red-600">{errorNow}</p>
            </div>
          )}

          {/* No file ID or ref fallback */}
          {!loadingNow && !errorNow && !citation?.source_file_id && !citation?.file_ref && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
              <FileText className="h-10 w-10 text-gray-300" />
              <div className="text-center">
                <p className="text-sm text-gray-600 font-medium">Evidence file could not be linked</p>
                <p className="text-xs text-gray-400 mt-1">Source: {citation?.source_file}</p>
              </div>
              {citation?.excerpt && (
                <div className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 max-w-lg">
                  <p className="text-xs font-medium text-blue-600 mb-1">Referenced excerpt:</p>
                  <p>"{citation.excerpt}"</p>
                </div>
              )}
            </div>
          )}

          {/* Zoomable image — transform wrapper fills full container so origin is its center */}
          {imgSrc && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: `translate(${view.panX}px, ${view.panY}px) scale(${view.zoom})`,
                transformOrigin: 'center center',
                transition: isDragging ? 'none' : 'transform 0.12s ease-out',
              }}
              onDoubleClick={resetView}
            >
              <img
                src={imgSrc}
                alt={imgAlt}
                draggable={false}
                className={imgClass}
                style={{ maxWidth: 'calc(100% - 32px)', maxHeight: 'calc(100% - 32px)' }}
              />
            </div>
          )}

          {/* "Generating annotation" overlay — stays fixed, not transformed */}
          {onCitedPage && annotationLoading && !annotatedUrl && viewMode === 'annotated' && !screenshotLoading && (citation?.source_file_id || citation?.file_ref) && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2.5 bg-purple-50 border border-purple-200 rounded-lg shadow-lg pointer-events-none">
              <Loader2 className="h-4 w-4 text-purple-500 animate-spin" />
              <span className="text-sm text-purple-700 font-medium">Generating AI annotation...</span>
            </div>
          )}
        </div>

        {/* Line items matched on the page being viewed (numbers match the Line Items table) */}
        {stepFindings?.page_items?.[page]?.length > 0 && (
          <div className="flex-shrink-0 border-t border-gray-200 bg-white px-5 py-2 max-h-28 overflow-y-auto" data-testid="viewer-page-items">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Line items on this page</p>
            <ul className="space-y-0.5">
              {stepFindings.page_items[page].map((it) => (
                <li
                  key={it.n}
                  className={`text-xs flex gap-2 ${it.n === stepFindings.focus_item ? 'font-semibold text-gray-900 bg-amber-50 rounded px-1' : 'text-gray-600'}`}
                >
                  <span className="font-mono text-gray-400 w-6 shrink-0">#{it.n}</span>
                  <span className={it.status === 'passed' ? 'text-green-600' : it.status === 'failed' ? 'text-red-500' : 'text-amber-500'}>
                    {it.status === 'passed' ? '✓' : it.status === 'failed' ? '✗' : '△'}
                  </span>
                  <span className="truncate" title={`${it.item_ref}${it.observation ? ` — ${it.observation}` : ''}`}>
                    {it.item_ref}{it.observation ? ` — ${it.observation}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-2.5 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <p className="text-xs text-gray-400">
              {hasAnnotation
                ? viewMode === 'annotated'
                  ? 'Viewing AI-annotated evidence — toggle to Original to verify source'
                  : 'Viewing original evidence screenshot'
                : annotationLoading
                  ? 'Generating AI annotation...'
                  : 'Evidence screenshot'
              }
            </p>
            {hasAnnotation && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded">
                AI annotations may alter visual appearance — always verify against original
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <p className="text-xs text-gray-300">Scroll to zoom · Zoomed: scroll or drag to move, Ctrl/⌘ + scroll to zoom · Double-click to reset</p>
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Close
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

export default EvidenceViewer;
